import React, { useEffect } from 'react'
import useReactRouter from 'use-react-router'
import { Table, Popconfirm, Layout, PageHeader } from 'antd'
import notification from 'mrx-notification'
import { ColumnsType } from 'antd/lib/table'
import useHeightResize from 'utils/useHeightResize'
import { Breadcrumbs } from 'components'
import * as rootStyles from 'assets/layoutStyle'
import styles from './SendgridSettingsList.module.scss'
import { connector, PropsFromRedux } from './container'
import { SendgridSetting } from '../../types'
import CreateSendgridSetting from '../CreateSendgridSetting'

type HeightResizeRule = [number, number, number]
const rules: HeightResizeRule[] = [
  [0, 800, 6],
  [800, 1000, 7],
  [1000, 1200, 9],
  [1200, Infinity, 10]
]

const SendgridSettingsList = (props: PropsFromRedux) => {
  const {
    sendgridSettingsList,
    isLoading,
    error,
    isNeedToFetchList,
    total,
    currentPage,

    changeCurrentPage,
    changePageSize,
    fetchSendgridSettingsList,
    removeSendgridSetting
  } = props

  const { history } = useReactRouter()

  const handleChangePageSize = (newSize: number) => {
    changePageSize(newSize)
    fetchSendgridSettingsList()
  }

  useHeightResize(handleChangePageSize, rules)

  useEffect(() => {
    if (error) {
      notification.error({
        message: 'Sendgrid settings list page error!',
        description: error
      })
    }
  }, [error])

  useEffect(() => {
    isNeedToFetchList && fetchSendgridSettingsList()
  }, [fetchSendgridSettingsList, isNeedToFetchList])

  const doConfirm = async (sendgridSettingId: number, e?: React.MouseEvent<HTMLElement>) => {
    e && e.stopPropagation()
    const isRemoved = await removeSendgridSetting(sendgridSettingId)
    if (isRemoved) {
      notification.success({
        message: 'Successfully',
        description: 'Sendgrid setting was deleted successfully!'
      })
    }
  }

  const columns: ColumnsType<SendgridSetting> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id'
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Api Key',
      dataIndex: 'apiKey',
      render: (record) => <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{record}</div>,
      key: 'apiKey'
    },
    {
      title: 'From',
      dataIndex: 'from',
      render: (record) => <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{record}</div>,
      key: 'from'
    },
    {
      title: 'From Name',
      dataIndex: 'fromName',
      render: (record) => <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{record}</div>,
      key: 'fromName'
    },
    {
      title: 'Action',
      dataIndex: '',
      key: 'x',
      render: (record: SendgridSetting) => (
        <span>
          <Popconfirm
            title='Are you sure delete this setting?'
            onConfirm={(e) => doConfirm(record.id, e)}
            onCancel={(e) => e?.stopPropagation()}
            okText='Yes'
            cancelText='No'
          >
            <a
              href='/#'
              onClick={(e) => {
                e.stopPropagation()
              }}
            >
              Remove
            </a>
          </Popconfirm>
        </span>
      )
    }
  ]

  return (
    <Layout className={styles.root}>
      <Layout.Header style={rootStyles.header}>
        <PageHeader onBack={() => history.goBack()} title='Sendgrid Settings List' extra={[<CreateSendgridSetting key='1' />]} />
      </Layout.Header>
      <Layout.Content style={rootStyles.contentBreadcrumbs}>
        <Breadcrumbs
          items={[
            { title: 'Main', url: '/' },
            { title: 'Settings', url: '/' },
            { title: 'Plugins', url: '' },
            { title: 'Sendgrid Settings', url: '/settings/plugins' }
          ]}
        />
      </Layout.Content>
      <Layout.Content className={styles.contentComponent}>
        <Table
          columns={columns}
          dataSource={sendgridSettingsList}
          bordered
          rowKey={(record) => record.id}
          loading={isLoading}
          pagination={{
            onChange: changeCurrentPage,
            current: currentPage,
            total
          }}
        />
      </Layout.Content>
    </Layout>
  )
}

export default connector(SendgridSettingsList)
